import React, { useMemo } from "react";
import { User, MapPin, ShieldCheck } from "lucide-react";
import type { FormValue } from "../../../redux/interfaces/folder.types";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { parseFormDate } from "../../../utils/vault/parseFormDate";

interface LockedFieldsSummaryProps {
  formValues: FormValue[];
  resolvedOcrFieldKeys: Set<string>;
}

// Keys that belong to the personal data section
const PERSONAL_KEYS = new Set([
  "first_name",
  "second_name",
  "first_last_name",
  "second_last_name",
  "curp",
  "rfc",
  "birth_date",
  "nationality",
  "gender",
]);

// Keys that belong to the address section
const ADDRESS_KEYS = new Set([
  "street",
  "exterior_number",
  "interior_number",
  "postal_code",
  "neighborhood",
  "city",
  "state",
]);

// Human-readable labels for each field key
const FIELD_LABELS: Record<string, string> = {
  first_name: "Nombre",
  second_name: "Segundo nombre",
  first_last_name: "Apellido paterno",
  second_last_name: "Apellido materno",
  curp: "CURP",
  rfc: "RFC",
  birth_date: "Fecha de nacimiento",
  nationality: "Nacionalidad",
  gender: "Genero",
  street: "Calle",
  exterior_number: "No. exterior",
  interior_number: "No. interior",
  postal_code: "Codigo postal",
  neighborhood: "Colonia",
  city: "Municipio",
  state: "Estado",
};

// Display order within each section
const PERSONAL_ORDER = [
  "first_name",
  "second_name",
  "first_last_name",
  "second_last_name",
  "curp",
  "rfc",
  "birth_date",
  "nationality",
  "gender",
];

const ADDRESS_ORDER = [
  "street",
  "exterior_number",
  "interior_number",
  "postal_code",
  "neighborhood",
  "city",
  "state",
];

interface FieldEntry {
  key: string;
  label: string;
  value: string;
}

const DataField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="space-y-0.5">
    <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide">
      {label}
    </dt>
    <dd className="text-sm text-slate-900 font-medium">{value}</dd>
  </div>
);

function formatDisplayValue(key: string, value: string): string {
  if (key === "birth_date" && value) {
    const parsed = parseFormDate(value);
    if (parsed) {
      return format(parsed, "dd/MM/yyyy", { locale: es });
    }
  }
  return value;
}

export const LockedFieldsSummary: React.FC<LockedFieldsSummaryProps> = ({
  formValues,
  resolvedOcrFieldKeys,
}) => {
  const { personalFields, addressFields } = useMemo(() => {
    // Build a map of key -> FormValue for locked fields with values
    const lockedWithValue = formValues.filter(
      (fv) => resolvedOcrFieldKeys.has(fv.key) && fv.value && fv.value.trim() !== ""
    );

    const fieldMap = new Map(lockedWithValue.map((fv) => [fv.key, fv]));

    const personal: FieldEntry[] = [];
    for (const key of PERSONAL_ORDER) {
      const fv = fieldMap.get(key);
      if (fv && PERSONAL_KEYS.has(key)) {
        personal.push({
          key,
          label: FIELD_LABELS[key] || fv.label,
          value: formatDisplayValue(key, fv.value),
        });
      }
    }

    const address: FieldEntry[] = [];
    for (const key of ADDRESS_ORDER) {
      const fv = fieldMap.get(key);
      if (fv && ADDRESS_KEYS.has(key)) {
        address.push({
          key,
          label: FIELD_LABELS[key] || fv.label,
          value: formatDisplayValue(key, fv.value),
        });
      }
    }

    return { personalFields: personal, addressFields: address };
  }, [formValues, resolvedOcrFieldKeys]);

  // If no locked fields have values, render nothing
  if (personalFields.length === 0 && addressFields.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4 text-emerald-600" strokeWidth={2} />
        <h4 className="text-sm font-semibold text-slate-800">
          Datos verificados
        </h4>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Estos datos fueron extraidos de tu identificacion oficial y no pueden ser editados.
      </p>

      <div className="space-y-4">
        {/* Personal data section */}
        {personalFields.length > 0 && (
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-4 h-4 text-slate-500" />
              <h4 className="text-sm font-semibold text-slate-700">
                Datos personales verificados
              </h4>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {personalFields.map((field) => (
                <DataField
                  key={field.key}
                  label={field.label}
                  value={field.value}
                />
              ))}
            </dl>
          </div>
        )}

        {/* Address section */}
        {addressFields.length > 0 && (
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-slate-500" />
              <h4 className="text-sm font-semibold text-slate-700">
                Direccion verificada
              </h4>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {addressFields.map((field) => (
                <DataField
                  key={field.key}
                  label={field.label}
                  value={field.value}
                />
              ))}
            </dl>
          </div>
        )}
      </div>
    </div>
  );
};
